import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { SimpleAlert } from "@src/appV2/redesign/components/SimpleAlert";
import { Input, Text, Title, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

import { ExtendShiftAdditionalDetailsField } from "./ExtendShiftAdditionalDetailsField";
import {
  extendShiftFormSchema,
  type ExtendShiftFormValues,
  formatShiftExtensionReason,
} from "./extendShiftFormSchema";
import { ExtendShiftReasonsField } from "./ExtendShiftReasonsField";

interface ExtendShiftBottomSheetProps {
  modalState: UseModalState;
  isSubmitting: boolean;
  onSubmit: (reason: string) => void;
}

export function ExtendShiftBottomSheet(props: ExtendShiftBottomSheetProps) {
  const { modalState, isSubmitting, onSubmit } = props;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<ExtendShiftFormValues>({
    resolver: zodResolver(extendShiftFormSchema),
    defaultValues: {
      hasSupervisorApproval: "",
      supervisorName: "",
      isSupervisorOnShift: "",
      inHouseStaffName: "",
      reasons: [],
      additionalDetails: "",
    },
    mode: "onChange",
  });

  const hasSupervisorApproval = watch("hasSupervisorApproval");
  const isSupervisorOnShift = watch("isSupervisorOnShift");

  const isSupervisorRequiredBlocked =
    hasSupervisorApproval === "no" && isSupervisorOnShift === "yes";

  const handleClose = useCallback(() => {
    reset();
    modalState.closeModal();
  }, [modalState, reset]);

  const handleFormSubmit = useCallback(() => {
    void handleSubmit((values: ExtendShiftFormValues) => {
      onSubmit(formatShiftExtensionReason(values));
    })();
  }, [handleSubmit, onSubmit]);

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <DialogContent sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}>
        <Stack spacing={5} sx={{ paddingY: 4 }}>
          <Stack spacing={2}>
            <Title semibold variant="h4" component="h1">
              Extend your shift
            </Title>
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
              Your workplace will review this form before extra time is approved for payment
            </Text>
          </Stack>

          <FormControl fullWidth>
            <FormLabel id="hasSupervisorApproval-label">
              <Text semibold variant="body2">
                Did you obtain approval from your supervisor?
              </Text>
            </FormLabel>
            <Controller
              name="hasSupervisorApproval"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  row
                  {...field}
                  aria-labelledby="hasSupervisorApproval-label"
                  onChange={(event) => {
                    field.onChange(event);
                    if (event.target.value === "yes") {
                      setValue("isSupervisorOnShift", "");
                      setValue("inHouseStaffName", "");
                    } else {
                      setValue("supervisorName", "");
                    }

                    void trigger();
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                    sx={{ marginY: 0 }}
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" sx={{ marginY: 0 }} />
                </RadioGroup>
              )}
            />
          </FormControl>

          {hasSupervisorApproval === "yes" && (
            <FormControl fullWidth>
              <FormLabel htmlFor="supervisorName-input">
                <Text semibold variant="body2">
                  Your supervisor&apos;s name:
                </Text>
              </FormLabel>
              <Controller
                name="supervisorName"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      fullWidth
                      {...field}
                      id="supervisorName-input"
                      placeholder="Supervisor name"
                    />
                    {fieldState.isTouched && isDefined(fieldState.error) && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
          )}

          {hasSupervisorApproval === "no" && (
            <FormControl fullWidth>
              <FormLabel id="isSupervisorOnShift-label">
                <Text semibold variant="body2">
                  Is there a supervisor on your shift?
                </Text>
              </FormLabel>
              <Controller
                name="isSupervisorOnShift"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    aria-labelledby="isSupervisorOnShift-label"
                    onChange={(event) => {
                      field.onChange(event);
                      if (event.target.value === "yes") {
                        setValue("inHouseStaffName", "");
                      }

                      void trigger();
                    }}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                )}
              />
            </FormControl>
          )}

          {isSupervisorRequiredBlocked && (
            <SimpleAlert
              severity="error"
              title="Supervisor approval required"
              description="You need to get a supervisor's approval before extending your shift"
            />
          )}

          {hasSupervisorApproval === "no" && isSupervisorOnShift === "no" && (
            <FormControl fullWidth>
              <FormLabel htmlFor="inHouseStaffName-input">
                <Text semibold variant="body2">
                  Name of an in-house staff who can confirm your extra time:
                </Text>
              </FormLabel>
              <Controller
                name="inHouseStaffName"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      fullWidth
                      {...field}
                      id="inHouseStaffName-input"
                      placeholder="Staff name"
                    />
                    {fieldState.isTouched && isDefined(fieldState.error) && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
          )}

          {!isSupervisorRequiredBlocked && (
            <>
              <ExtendShiftReasonsField control={control} />
              <ExtendShiftAdditionalDetailsField control={control} watch={watch} />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogFooter
        withShadow
        orientation="horizontal"
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}
      >
        <IconButton
          variant="outlined"
          iconType="arrow-left"
          size="large"
          aria-label="Go back"
          onClick={handleClose}
        />
        <Button
          sx={{ minWidth: 180 }}
          variant="contained"
          size="large"
          disabled={!isValid || isSubmitting || isSupervisorRequiredBlocked}
          onClick={handleFormSubmit}
        >
          Submit
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
